import { motion } from 'framer-motion';
import { Shield, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-home">
            <div className="w-10 h-10 rounded-lg bg-indigo-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold">Protego AI</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-gray-400 mb-12">Protego AI Inc. | section3.ai | Effective: March 2, 2026</p>

          <div className="prose prose-invert prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="text-gray-300 leading-relaxed">
                Protego AI Inc. ("we," "us," or "our") operates the website section3.ai and provides the Protego AI account health monitoring platform (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service. Please read this policy carefully. By accessing or using our Service, you acknowledge that you have read and understood this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-medium text-white mb-3">2.1 Information You Provide</h3>
              <p className="text-gray-300 leading-relaxed mb-4">We may collect information you voluntarily provide when you:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>Register for an account or request a demo</li>
                <li>Complete lead capture forms at trade shows or on our website</li>
                <li>Contact us by email or through our contact forms</li>
                <li>Subscribe to our newsletter or educational content</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                This may include your name, email address, company name, Amazon Seller ID, phone number, and any other information you choose to provide.
              </p>

              <h3 className="text-xl font-medium text-white mb-3 mt-6">2.2 Amazon Selling Partner API Data</h3>
              <p className="text-gray-300 leading-relaxed">
                To provide our account health monitoring service, we request your authorization to access your Amazon seller data through the Amazon Selling Partner API (SP-API). This may include order performance metrics, account health ratings, policy warnings, inventory data, customer feedback metrics, and other data Amazon makes available through the SP-API. We only access the data necessary to deliver the Service.
              </p>

              <h3 className="text-xl font-medium text-white mb-3 mt-6">2.3 Automatically Collected Information</h3>
              <p className="text-gray-300 leading-relaxed">
                When you visit our website, we may automatically collect certain information, including your IP address, browser type and version, operating system, pages visited, time spent on pages, referring URLs, and device information.
              </p>

              <h3 className="text-xl font-medium text-white mb-3 mt-6">2.4 Cookies and Tracking Technologies</h3>
              <p className="text-gray-300 leading-relaxed">
                We use cookies and similar tracking technologies to improve your experience on our website, analyze usage patterns, and deliver relevant content. You may control cookie settings through your browser preferences, though disabling cookies may affect the functionality of our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-300 leading-relaxed mb-4">We use the information we collect to:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>Provide, operate, and improve the Service</li>
                <li>Analyze your Amazon account health data and generate risk reports and alerts</li>
                <li>Communicate with you about your account, updates, and promotional content</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Conduct research and analysis to improve our platform and services</li>
                <li>Send you educational content about Amazon account health and policy enforcement</li>
                <li>Comply with legal obligations and enforce our Terms of Service</li>
                <li>Detect and prevent fraud, abuse, and security incidents</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. How We Share Your Information</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We do not sell, rent, or trade your personal information to third parties. We may share your information in the following circumstances:
              </p>

              <h3 className="text-xl font-medium text-white mb-3">4.1 Service Providers</h3>
              <p className="text-gray-300 leading-relaxed">
                We may share your information with trusted third-party service providers who assist us in operating our business, including cloud hosting providers, analytics services, email delivery services, and customer support tools. These providers are contractually obligated to protect your information and may only use it to perform services on our behalf.
              </p>

              <h3 className="text-xl font-medium text-white mb-3 mt-6">4.2 Business Transfers</h3>
              <p className="text-gray-300 leading-relaxed">
                If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you via email or prominent notice on our website of any change in ownership or use of your information.
              </p>

              <h3 className="text-xl font-medium text-white mb-3 mt-6">4.3 Legal Requirements</h3>
              <p className="text-gray-300 leading-relaxed">
                We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).
              </p>

              <h3 className="text-xl font-medium text-white mb-3 mt-6">4.4 Protection of Rights</h3>
              <p className="text-gray-300 leading-relaxed">
                We may disclose your information where we believe it is necessary to investigate, prevent, or take action regarding potential violations of our policies, suspected fraud, situations involving potential threats to safety, or as evidence in litigation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Data Retention</h2>
              <p className="text-gray-300 leading-relaxed">
                We retain your personal information for as long as necessary to provide the Service, fulfill the purposes outlined in this Privacy Policy, and comply with legal obligations. Amazon SP-API data is retained for the duration of your active account and for up to 12 months following account termination, unless a shorter retention period is required by Amazon's data usage policies or applicable law. You may request deletion of your data at any time by contacting us at legal@section3.ai.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Data Security</h2>
              <p className="text-gray-300 leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Your Rights and Choices</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request transfer of your data in a structured, machine-readable format</li>
                <li><strong>Objection:</strong> Object to certain processing activities, including marketing communications</li>
                <li><strong>Withdrawal of Consent:</strong> Withdraw consent for SP-API access at any time through your Amazon Seller Central settings</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                To exercise any of these rights, please contact us at <a href="mailto:hello@protegoos.com" className="text-indigo-400 hover:text-indigo-300">hello@protegoos.com</a>. We will respond to your request within 30 days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Children's Privacy</h2>
              <p className="text-gray-300 leading-relaxed">
                Our Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information promptly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Third-Party Links</h2>
              <p className="text-gray-300 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party websites you visit.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Amazon's Policies</h2>
              <p className="text-gray-300 leading-relaxed">
                Our collection and use of data obtained through the Amazon SP-API is subject to Amazon's Selling Partner API Developer Agreement and data protection policies. We comply with all applicable Amazon policies regarding the use and storage of seller data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-300 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page with an updated effective date. Your continued use of the Service after such changes constitutes your acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Contact Us</h2>
              <p className="text-gray-300 leading-relaxed">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="mt-4 p-6 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white font-semibold">Protego AI Inc.</p>
                <p className="text-gray-300 mt-2">Email: <a href="mailto:hello@protegoos.com" className="text-indigo-400 hover:text-indigo-300">hello@protegoos.com</a></p>
                <p className="text-gray-300">Website: <a href="https://section3.ai" className="text-indigo-400 hover:text-indigo-300">https://section3.ai</a></p>
              </div>
            </section>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 text-center text-gray-500 text-sm">
            Protego AI Inc. — section3.ai | Privacy Policy
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
